<template>
  <div class="hh-page">
    <!-- ===== HERO ===== -->
    <section class="hh-hero section--dark">
      <div class="hh-hero__bg">
        <div class="hh-hero__shape hh-hero__shape--1"></div>
        <div class="hh-hero__shape hh-hero__shape--2"></div>
      </div>
      <div class="section-container">
        <div class="hh-hero__grid">
          <div class="hh-hero__content">
            <span class="badge-label badge-label--light">
              <span class="badge-label__dot"></span>
              HomeHub · Closed Beta
            </span>
            <h1 class="hh-hero__title">
              Satu aplikasi untuk <span class="hh-hero__highlight">semua urusan rumah</span>
            </h1>
            <p class="hh-hero__subtitle">
              Keuangan, tagihan, agenda, daftar belanja, perawatan rumah, hingga obrolan keluarga, semuanya di satu
              tempat yang dipakai bersama seisi rumah. Dibuat khusus untuk keluarga Indonesia.
            </p>
            <div class="hh-hero__actions">
              <router-link :to="betaLink" class="btn-primary-custom custom-link">Gabung Beta Gratis</router-link>
              <router-link to="/#fitur" class="btn-outline-light custom-link">Lihat Fitur</router-link>
            </div>
            <div class="hh-hero__platforms">
              <span><i class="fa fa-android"></i> Android</span>
              <span><i class="fa fa-apple"></i> iOS</span>
              <span><i class="fa fa-globe"></i> Web</span>
            </div>
          </div>

          <div class="hh-hero__visual">
            <div class="hh-hero__phone hh-hero__phone--back">
              <PhoneMockup screen="agenda" compact />
            </div>
            <div class="hh-hero__phone hh-hero__phone--front">
              <PhoneMockup screen="home" />
            </div>
            <div class="hh-float hh-float--1">
              <span class="hh-float__icon"><i class="fa fa-check"></i></span>
              <div>
                <strong>Tagihan PLN lunas</strong>
                <span>Rp489.000 · tercatat ke Kas</span>
              </div>
            </div>
            <div class="hh-float hh-float--2">
              <span class="hh-float__icon hh-float__icon--alt"><i class="fa fa-bell"></i></span>
              <div>
                <strong>Servis AC 3 hari lagi</strong>
                <span>Ditugaskan ke Pak Joko</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== PROBLEM ===== -->
    <section class="hh-problem section">
      <div class="section-container">
        <div class="section-title animate-on-scroll">
          <span class="badge-label">
            <span class="badge-label__dot"></span>
            Kenapa HomeHub?
          </span>
          <h2>Urusan rumah tersebar di <span class="text-gradient">terlalu banyak tempat</span></h2>
          <p class="section-subtitle">
            Grup WhatsApp, aplikasi catatan, m-banking, spreadsheet, dan ingatan masing-masing. Akibatnya ada yang
            terlewat.
          </p>
        </div>
        <div class="hh-problem__grid">
          <div v-for="(p, index) in problems" :key="p.title" class="hh-problem__card animate-on-scroll"
            :class="'delay-' + (index + 1)">
            <div class="hh-problem__icon"><i :class="p.icon"></i></div>
            <h4>{{ p.title }}</h4>
            <div class="hh-problem__text">{{ p.text }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== FEATURES ===== -->
    <section id="fitur" class="hh-features section section--alt">
      <div class="section-container">
        <div class="section-title animate-on-scroll">
          <span class="badge-label">
            <span class="badge-label__dot"></span>
            Fitur
          </span>
          <h2>Semua yang dibutuhkan <span class="text-gradient">rumah tangga modern</span></h2>
          <p class="section-subtitle">
            Setiap fitur berguna sendiri-sendiri, dan jadi lebih kuat karena saling terhubung.
          </p>
        </div>
        <div class="hh-features__grid">
          <div v-for="(f, index) in features" :key="f.title" class="card-bexon hh-feature animate-on-scroll"
            :class="'delay-' + ((index % 3) + 1)">
            <span v-if="f.soon" class="hh-feature__soon">Segera hadir</span>
            <div class="card-bexon__icon"><i :class="f.icon"></i></div>
            <h4 class="card-bexon__title">{{ f.title }}</h4>
            <div class="card-bexon__text">{{ f.text }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== SHOWCASE ===== -->
    <section id="tampilan" class="hh-showcase section">
      <div class="section-container">
        <div class="section-title animate-on-scroll">
          <span class="badge-label">
            <span class="badge-label__dot"></span>
            Tampilan Aplikasi
          </span>
          <h2>Intip <span class="text-gradient">HomeHub</span></h2>
          <p class="section-subtitle">Ramah dipakai satu tangan, dengan bahasa sehari-hari, bukan bahasa bank.</p>
        </div>

        <div class="hh-showcase__grid animate-on-scroll">
          <div class="hh-showcase__tabs" role="tablist">
            <button v-for="(s, index) in showcase" :key="s.screen" type="button" role="tab"
              class="hh-showcase__tab" :class="{ 'hh-showcase__tab--active': activeShowcase === index }"
              :aria-selected="String(activeShowcase === index)" @click="activeShowcase = index">
              <span class="hh-showcase__tab-icon"><i :class="s.icon"></i></span>
              <span class="hh-showcase__tab-text">
                <strong>{{ s.title }}</strong>
                <span>{{ s.tagline }}</span>
              </span>
            </button>
          </div>

          <div class="hh-showcase__stage">
            <div class="hh-showcase__glow"></div>
            <transition name="hh-swap" mode="out-in">
              <PhoneMockup :key="currentShowcase.screen" :screen="currentShowcase.screen" />
            </transition>
          </div>

          <transition name="hh-swap" mode="out-in">
            <div :key="currentShowcase.screen" class="hh-showcase__detail">
              <h3>{{ currentShowcase.title }}</h3>
              <div class="hh-showcase__desc">{{ currentShowcase.description }}</div>
              <div class="hh-check-list">
                <div v-for="point in currentShowcase.points" :key="point" class="hh-check-list__item">
                  <i class="fa fa-check-circle"></i>
                  <span>{{ point }}</span>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </section>

    <!-- ===== CONNECTED ===== -->
    <section class="hh-connected section section--alt">
      <div class="section-container">
        <div class="section-title animate-on-scroll">
          <span class="badge-label">
            <span class="badge-label__dot"></span>
            Saling Terhubung
          </span>
          <h2>Catat sekali, <span class="text-gradient">semuanya ikut terupdate</span></h2>
          <p class="section-subtitle">Tidak perlu input berulang. Satu aksi otomatis mengalir ke modul lain.</p>
        </div>
        <div class="hh-connected__grid">
          <div v-for="(flow, index) in flows" :key="flow.trigger" class="hh-flow animate-on-scroll"
            :class="'delay-' + (index + 1)">
            <div class="hh-flow__trigger">
              <span class="hh-flow__icon"><i :class="flow.icon"></i></span>
              <strong>{{ flow.trigger }}</strong>
            </div>
            <div class="hh-flow__results">
              <div v-for="r in flow.results" :key="r" class="hh-flow__result">
                <i class="fa fa-long-arrow-right"></i>
                <span>{{ r }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== ROLES & PRIVACY ===== -->
    <section id="privasi" class="hh-privacy section section--dark">
      <div class="hh-privacy__shape"></div>
      <div class="section-container">
        <div class="hh-privacy__grid">
          <div class="animate-on-scroll">
            <span class="badge-label badge-label--light">
              <span class="badge-label__dot"></span>
              Peran &amp; Privasi
            </span>
            <h2>Dipakai bersama, <span class="hh-hero__highlight">privasi tetap terjaga</span></h2>
            <p class="hh-privacy__lead">
              Rumah adalah ruang bersama, tapi tidak semua hal harus dilihat semua orang. Setiap anggota punya peran,
              dan setiap catatan punya pengaturan siapa yang boleh melihat.
            </p>
            <div class="hh-roles">
              <div v-for="role in roles" :key="role.name" class="hh-role">
                <i :class="role.icon"></i>
                <div>
                  <strong>{{ role.name }}</strong>
                  <span>{{ role.desc }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="hh-privacy__side animate-on-scroll delay-2">
            <div class="hh-visibility">
              <div v-for="v in visibility" :key="v.name" class="hh-visibility__item">
                <span class="hh-visibility__icon"><i :class="v.icon"></i></span>
                <div>
                  <strong>{{ v.name }}</strong>
                  <span>{{ v.desc }}</span>
                </div>
              </div>
            </div>
            <div class="hh-trust">
              <div v-for="t in trust" :key="t" class="hh-trust__item">
                <i class="fa fa-shield"></i>
                <span>{{ t }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== PRICING ===== -->
    <section id="harga" class="hh-pricing section">
      <div class="section-container">
        <div class="section-title animate-on-scroll">
          <span class="badge-label">
            <span class="badge-label__dot"></span>
            Harga
          </span>
          <h2>Satu langganan untuk <span class="text-gradient">satu rumah</span></h2>
          <p class="section-subtitle">
            Bayar per rumah, bukan per orang. Mulai gratis, tingkatkan saat keluarga Anda bertambah.
          </p>
        </div>

        <div class="hh-pricing__grid">
          <div v-for="(plan, index) in plans" :key="plan.name" class="hh-plan animate-on-scroll"
            :class="['delay-' + (index + 1), { 'hh-plan--featured': plan.featured }]">
            <span v-if="plan.featured" class="hh-plan__ribbon">Paling lengkap</span>
            <div class="hh-plan__name">{{ plan.name }}</div>
            <div class="hh-plan__tagline">{{ plan.tagline }}</div>
            <div class="hh-plan__price">
              <strong>{{ plan.price }}</strong>
              <span>{{ plan.period }}</span>
            </div>
            <div class="hh-plan__note">{{ plan.note }}</div>
            <div class="hh-plan__seats">
              <i class="fa fa-users"></i> Hingga <strong>{{ plan.seats }} anggota</strong> per rumah
            </div>
            <div class="hh-plan__features">
              <div v-for="item in plan.items" :key="item.label" class="hh-plan__feature"
                :class="{ 'hh-plan__feature--off': !item.included }">
                <i :class="item.included ? 'fa fa-check' : 'fa fa-minus'"></i>
                <span>{{ item.label }}</span>
              </div>
            </div>
            <router-link :to="betaLink" class="custom-link"
              :class="plan.featured ? 'hh-plan__btn hh-plan__btn--primary' : 'hh-plan__btn'">
              {{ plan.cta }}
            </router-link>
          </div>
        </div>
        <div class="hh-pricing__footnote animate-on-scroll">
          Kursi dihitung dari anggota aktif dan undangan yang belum diterima. Jika langganan berakhir, tidak ada data
          yang dihapus. Anggota di atas batas paket Gratis hanya bisa melihat sampai langganan diperpanjang.
        </div>
      </div>
    </section>

    <!-- ===== FAQ ===== -->
    <section id="faq" class="hh-faq section section--alt">
      <div class="section-container">
        <div class="hh-faq__grid animate-on-scroll">
          <div class="hh-faq__header">
            <span class="badge-label">
              <span class="badge-label__dot"></span>
              FAQ
            </span>
            <h2>Pertanyaan yang Sering <span class="text-gradient">Diajukan</span></h2>
            <p>Belum menemukan jawabannya? <router-link to="/contact" class="hh-inline-link">Hubungi kami</router-link>.
            </p>
          </div>
          <div>
            <div v-for="(faq, index) in faqs" :key="faq.question" class="accordion-item-custom"
              :class="{ active: activeFaq === index }">
              <div class="accordion-header-custom" @click="toggleFaq(index)">
                <span>{{ faq.question }}</span>
                <span class="accordion-icon">+</span>
              </div>
              <div class="accordion-body-custom">
                <p>{{ faq.answer }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== CTA ===== -->
    <section class="hh-cta section--dark">
      <div class="hh-cta__shape hh-cta__shape--1"></div>
      <div class="hh-cta__shape hh-cta__shape--2"></div>
      <div class="section-container">
        <div class="hh-cta__content animate-on-scroll">
          <span class="badge-label badge-label--light">
            <span class="badge-label__dot"></span>
            Kuota beta terbatas
          </span>
          <h2>Ajak seisi rumah mencoba <span style="color: #C73468;">HomeHub</span></h2>
          <p>
            Kami membuka Closed Beta untuk keluarga yang ingin ikut membentuk HomeHub. Daftar sekarang dan dapatkan
            Family Plus gratis selama masa beta.
          </p>
          <div class="hh-cta__actions">
            <router-link :to="betaLink" class="btn-primary-custom custom-link">Daftar Beta</router-link>
            <a href="https://wa.me/628114938580" class="btn-outline-light custom-link">
              <i class="fa fa-whatsapp"></i> Tanya via WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import PhoneMockup from "@/components/homehub/PhoneMockup";

export default {
  name: "HomePage",
  components: { PhoneMockup },
  data() {
    return {
      betaLink: { path: "/contact", query: { subject: "Daftar Beta HomeHub" } },
      activeShowcase: 0,
      activeFaq: 0,
      problems: [
        {
          icon: "fa fa-comments-o",
          title: "Info tenggelam di grup chat",
          text: "Jadwal, daftar belanja, dan pengingat hilang di antara ratusan pesan.",
        },
        {
          icon: "fa fa-bolt",
          title: "Tagihan sering telat",
          text: "PLN, PDAM, pajak kendaraan, PBB. Tidak ada yang merasa bertanggung jawab mengingatkan.",
        },
        {
          icon: "fa fa-exchange",
          title: "Uang bersama campur pribadi",
          text: "Sulit tahu siapa sudah bayar apa, dan siapa masih berutang ke siapa.",
        },
        {
          icon: "fa fa-user-times",
          title: "Aplikasi lain untuk satu orang",
          text: "Kebanyakan aplikasi dibuat untuk individu, bukan untuk seisi rumah.",
        },
      ],
      features: [
        {
          icon: "fa fa-pie-chart",
          title: "Kas & Anggaran",
          text: "Catat pemasukan dan pengeluaran, pisahkan uang bersama dan pribadi, atur anggaran per kategori dengan peringatan 80% dan 100%.",
        },
        {
          icon: "fa fa-calendar",
          title: "Agenda Keluarga",
          text: "Kalender bersama dengan acara berulang, RSVP, tugas rumah, ulang tahun, dan libur nasional.",
        },
        {
          icon: "fa fa-shopping-basket",
          title: "Daftar Belanja & Catatan",
          text: "Sinkron real-time dan tetap bisa dipakai offline. Selesai belanja, langsung tercatat sebagai pengeluaran.",
        },
        {
          icon: "fa fa-wrench",
          title: "Perawatan Rumah",
          text: "Jadwal servis AC, kuras toren, servis kendaraan, pajak STNK, dan PBB, lengkap dengan buku kontak tukang.",
        },
        {
          icon: "fa fa-handshake-o",
          title: "Utang & Piutang",
          text: "Lacak pinjaman antaranggota maupun ke pihak luar, cicilan, dan pengingat jatuh tempo yang sopan.",
          soon: true,
        },
        {
          icon: "fa fa-diamond",
          title: "Aset & Kekayaan Bersih",
          text: "Rumah, kendaraan, emas, reksa dana, deposito, dan polis asuransi beserta dokumennya dalam satu tempat.",
          soon: true,
        },
        {
          icon: "fa fa-book",
          title: "Jurnal Keluarga",
          text: "Abadikan momen dengan foto, tandai anggota, dan dapatkan kenangan \"hari ini di tahun lalu\".",
          soon: true,
        },
        {
          icon: "fa fa-bolt",
          title: "Bayar Tagihan",
          text: "Cek dan bayar PLN pascabayar, token listrik, dan PDAM dalam sekali ketuk via QRIS, VA, atau e-wallet.",
          soon: true,
        },
        {
          icon: "fa fa-comments",
          title: "Chat Keluarga",
          text: "Obrolan dengan kartu interaktif: RSVP acara, tandai tagihan lunas, atau centang daftar belanja langsung dari chat.",
          soon: true,
        },
        {
          icon: "fa fa-plane",
          title: "Trip & Itinerary",
          text: "Rencanakan liburan atau mudik: itinerary harian, anggaran trip, daftar bawaan, dan dokumen perjalanan.",
          soon: true,
        },
        {
          icon: "fa fa-gamepad",
          title: "Mini Games Keluarga",
          text: "Kuis keluarga, tebak gambar, dan sambung kata untuk malam main bersama, dari HP masing-masing.",
          soon: true,
        },
        {
          icon: "fa fa-bell",
          title: "Pengingat Pintar",
          text: "Notifikasi sesuai preferensi tiap anggota, dengan jam tenang dan tanpa isi sensitif di layar kunci.",
        },
      ],
      showcase: [
        {
          screen: "budget",
          icon: "fa fa-pie-chart",
          title: "Kas & Anggaran",
          tagline: "Tahu ke mana uang pergi",
          description:
            "Lihat pengeluaran bersama bulan ini, kategori mana yang hampir habis, dan transaksi yang masuk otomatis dari fitur lain.",
          points: [
            "Mode Bersama dan Pribadi dalam satu ketukan",
            "Periode anggaran mengikuti tanggal gajian",
            "Laporan per kategori, per anggota, dan arus kas",
          ],
        },
        {
          screen: "bills",
          icon: "fa fa-bolt",
          title: "Bayar Tagihan",
          tagline: "PLN & PDAM tanpa ribet",
          description:
            "Simpan ID pelanggan sekali, cek tagihan terbaru beserta nama pelanggan, lalu bayar. Struk dan pengeluaran tercatat otomatis.",
          points: [
            "Rincian biaya admin ditampilkan sebelum bayar",
            "Token listrik prabayar tersimpan di riwayat",
            "Pengingat 5, 2, dan 0 hari sebelum jatuh tempo",
          ],
        },
        {
          screen: "agenda",
          icon: "fa fa-calendar",
          title: "Agenda Keluarga",
          tagline: "Semua jadwal, satu kalender",
          description:
            "Jadwal sekolah, arisan, les, dan tugas rumah dalam satu kalender. Jatuh tempo tagihan dan servis ikut muncul.",
          points: [
            "RSVP Hadir / Mungkin / Tidak untuk setiap acara",
            "Libur nasional langsung tampil di kalender",
            "Sinkron ke Google Calendar atau Apple Calendar",
          ],
        },
        {
          screen: "shopping",
          icon: "fa fa-shopping-basket",
          title: "Daftar Belanja",
          tagline: "Real-time, bahkan offline",
          description:
            "Seisi rumah bisa menambah dan mencentang barang bersamaan. Asisten rumah tangga bisa diberi akses hanya ke daftar tertentu.",
          points: [
            "Lihat siapa yang mencentang, saat itu juga",
            "Tetap jalan tanpa sinyal di supermarket",
            "Selesai belanja → pengeluaran + foto struk",
          ],
        },
        {
          screen: "maintenance",
          icon: "fa fa-wrench",
          title: "Perawatan Rumah",
          tagline: "Tidak ada lagi yang terlewat",
          description:
            "Template perawatan khas rumah Indonesia sudah siap pakai. Jadwal berikutnya dihitung dari pengerjaan terakhir.",
          points: [
            "Servis AC, toren, pompa air, kendaraan, STNK, PBB",
            "Tugaskan ke anggota, asisten rumah tangga, atau tukang",
            "Hubungi tukang lewat WhatsApp sekali ketuk",
          ],
        },
        {
          screen: "chat",
          icon: "fa fa-comments",
          title: "Chat Keluarga",
          tagline: "Ngobrol sambil beres",
          description:
            "Bagikan tagihan, acara, atau daftar belanja ke chat sebagai kartu interaktif yang selalu menampilkan status terbaru.",
          points: [
            "RSVP dan tandai lunas langsung dari kartu",
            "Kanal khusus, misalnya hanya orang tua",
            "Kartu menghormati hak akses tiap anggota",
          ],
        },
      ],
      flows: [
        {
          icon: "fa fa-snowflake-o",
          trigger: "Servis AC selesai",
          results: ["Biaya masuk ke Kas & Anggaran", "Jadwal servis berikutnya dihitung ulang", "Agenda ikut diperbarui"],
        },
        {
          icon: "fa fa-shopping-basket",
          trigger: "Selesai belanja",
          results: ["Pengeluaran Belanja Dapur tercatat", "Foto struk tersimpan", "Barang tercentang diarsipkan"],
        },
        {
          icon: "fa fa-plane",
          trigger: "Trip mudik dibuat",
          results: ["Blok hari di Agenda", "Anggaran trip & daftar bawaan", "Grup chat khusus peserta"],
        },
        {
          icon: "fa fa-bolt",
          trigger: "Tagihan PLN dibayar",
          results: ["Pengeluaran Listrik tercatat", "Jatuh tempo di Agenda ditandai selesai", "Struk dikirim ke pembayar"],
        },
      ],
      roles: [
        { icon: "fa fa-star", name: "Pemilik & Admin", desc: "Atur anggota, keuangan, dan langganan" },
        { icon: "fa fa-user", name: "Dewasa", desc: "Akses penuh ke semua fitur harian" },
        { icon: "fa fa-graduation-cap", name: "Remaja", desc: "Uang saku dan pengeluaran sendiri" },
        { icon: "fa fa-child", name: "Anak", desc: "Agenda, tugas, catatan, dan games" },
        { icon: "fa fa-id-badge", name: "Asisten Rumah Tangga & Sopir", desc: "Daftar belanja dan tugas yang ditugaskan" },
        { icon: "fa fa-user-plus", name: "Tamu", desc: "Hanya trip atau acara yang dibagikan" },
      ],
      visibility: [
        { icon: "fa fa-lock", name: "Pribadi", desc: "Hanya Anda. Default untuk jurnal dan utang." },
        { icon: "fa fa-user-circle", name: "Anggota tertentu", desc: "Pilih siapa saja yang boleh melihat." },
        { icon: "fa fa-home", name: "Seisi rumah", desc: "Terlihat oleh semua anggota yang berhak." },
      ],
      trust: [
        "HomeHub tidak pernah menyimpan uang Anda. Pembayaran lewat mitra berlisensi.",
        "Data terenkripsi dan setiap rumah terisolasi satu sama lain.",
        "Notifikasi tidak menampilkan isi sensitif di layar kunci.",
        "Dirancang mengikuti UU Pelindungan Data Pribadi.",
      ],
      plans: [
        {
          name: "Gratis",
          tagline: "Untuk keluarga kecil yang baru mulai",
          price: "Rp0",
          period: "selamanya",
          note: "Tanpa kartu kredit.",
          seats: 4,
          cta: "Mulai Gratis",
          items: [
            { label: "Kas, anggaran, agenda, daftar & perawatan", included: true },
            { label: "Sinkron real-time & mode offline", included: true },
            { label: "Penyimpanan foto & dokumen 2 GB", included: true },
            { label: "Ekspor riwayat hingga 3 bulan", included: true },
            { label: "Bayar tagihan otomatis", included: false },
            { label: "Video di jurnal keluarga", included: false },
          ],
        },
        {
          name: "Family Plus",
          tagline: "Untuk keluarga besar, asisten rumah tangga, dan kakek-nenek",
          price: "Rp199.000",
          period: "/ rumah / tahun",
          note: "Setara ±Rp17 ribu per bulan untuk seisi rumah.",
          seats: 20,
          featured: true,
          cta: "Coba Gratis Selama Beta",
          items: [
            { label: "Semua fitur paket Gratis", included: true },
            { label: "Penyimpanan foto & dokumen 50 GB", included: true },
            { label: "Riwayat & ekspor tanpa batas (CSV/XLSX)", included: true },
            { label: "Bayar tagihan otomatis dengan batas nominal", included: true },
            { label: "Mini games & video di jurnal", included: true },
            { label: "Prioritas dukungan", included: true },
          ],
        },
      ],
      faqs: [
        {
          question: "Kapan HomeHub bisa digunakan?",
          answer:
            "HomeHub sedang memasuki Closed Beta bersama keluarga-keluarga pertama. Daftar sekarang untuk mendapat undangan. Fitur dirilis bertahap, dimulai dari Agenda, Daftar Belanja, Perawatan Rumah, dan Kas & Anggaran.",
        },
        {
          question: "Apakah HomeHub menyimpan uang saya?",
          answer:
            "Tidak. HomeHub hanya mencatat keuangan Anda. Saat membayar tagihan, pembayaran diproses oleh payment gateway dan mitra PPOB berlisensi, dan HomeHub tidak pernah menampung dana Anda.",
        },
        {
          question: "Siapa yang bisa melihat pengeluaran pribadi saya?",
          answer:
            "Hanya Anda. Setiap catatan punya pengaturan visibilitas: Pribadi, Anggota tertentu, atau Seisi rumah. Total dan laporan juga hanya dihitung dari catatan yang boleh dilihat oleh orang yang membukanya.",
        },
        {
          question: "Bisakah asisten rumah tangga atau sopir ikut diundang?",
          answer:
            "Bisa. Peran Asisten Rumah Tangga & Sopir hanya bisa mengakses daftar belanja, tugas yang ditugaskan, dan agenda yang dibagikan, tanpa akses ke keuangan, jurnal, utang, maupun aset.",
        },
        {
          question: "Bisakah saya bergabung di lebih dari satu rumah?",
          answer:
            "Bisa. Satu akun dapat menjadi anggota di beberapa rumah, misalnya rumah sendiri dan rumah orang tua, lalu berpindah dengan sekali ketuk.",
        },
        {
          question: "Apa yang terjadi jika langganan Family Plus berakhir?",
          answer:
            "Tidak ada data yang dihapus. Ada masa tenggang 7 hari, lalu anggota di atas batas 4 orang (yang paling baru bergabung) menjadi hanya-lihat sampai langganan diperpanjang.",
        },
      ],
    };
  },
  computed: {
    currentShowcase() {
      return this.showcase[this.activeShowcase];
    },
  },
  mounted() {
    this.initScrollAnimations();
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    initScrollAnimations() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("animated");
            }
          });
        },
        { threshold: 0.1 }
      );
      this.$nextTick(() => {
        this.$el.querySelectorAll(".animate-on-scroll").forEach((el) => {
          this.observer.observe(el);
        });
      });
    },
    toggleFaq(index) {
      this.activeFaq = this.activeFaq === index ? -1 : index;
    },
  },
};
</script>

<style scoped lang="scss">
.text-gradient {
  background: $brand-gradient;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.btn-outline-light {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all $transition-base;

  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
  }
}

.v-application a.custom-link {
  color: inherit !important;
}

.v-application a.hh-inline-link {
  color: $brand-magenta;
  font-weight: 600;
}

// ===== HERO =====
.hh-hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 120px 0 80px;
  overflow: hidden;
}

.hh-hero__bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.hh-hero__shape {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);

  &--1 {
    top: -20%;
    right: -10%;
    width: 650px;
    height: 650px;
    background: rgba(108, 41, 115, 0.3);
  }

  &--2 {
    bottom: -30%;
    left: -15%;
    width: 500px;
    height: 500px;
    background: rgba(199, 52, 104, 0.15);
  }
}

.hh-hero__grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 48px;
  align-items: center;
}

.hh-hero__title {
  font-size: 3.4rem;
  font-weight: 800;
  color: #fff;
  line-height: 1.12;
  margin-bottom: 20px;
}

.hh-hero__highlight {
  background: linear-gradient(135deg, $brand-magenta, $brand-pink);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hh-hero__subtitle {
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.8;
  margin-bottom: 32px;
  max-width: 520px;
}

.hh-hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 36px;
}

.hh-hero__platforms {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.85rem;
    font-weight: 500;
  }

  i {
    font-size: 1.05rem;
  }
}

.hh-hero__visual {
  position: relative;
  height: 640px;
}

.hh-hero__phone {
  position: absolute;

  &--front {
    top: 10px;
    left: 8%;
    z-index: 2;
  }

  &--back {
    top: 70px;
    right: 0;
    z-index: 1;
    opacity: 0.75;
    transform: rotate(6deg);
  }
}

.hh-float {
  position: absolute;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 14px;
  background: rgba(26, 10, 46, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  animation: hh-float 4s ease-in-out infinite;

  strong {
    display: block;
    color: #fff;
    font-size: 0.85rem;
  }

  span {
    color: rgba(255, 255, 255, 0.55);
    font-size: 0.75rem;
  }

  &--1 {
    bottom: 10px;
    left: -40px;
  }

  &--2 {
    top: 30px;
    right: -10px;
    animation-delay: 2s;
  }
}

.hh-float__icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #2f9e7a, #44c495);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--alt {
    background: $brand-gradient;
  }
}

@keyframes hh-float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

// ===== PROBLEM =====
.hh-problem__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.hh-problem__card {
  padding: 28px 24px;
  border-radius: $radius-lg;
  background: $light-bg-alt;
  border: 1px solid rgba(108, 41, 115, 0.06);

  h4 {
    font-size: 1.05rem;
    margin-bottom: 8px;
  }
}

.hh-problem__icon {
  width: 48px;
  height: 48px;
  border-radius: $radius-md;
  background: rgba(199, 52, 104, 0.1);
  color: $brand-magenta;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin-bottom: 18px;
}

.hh-problem__text {
  font-size: 0.9rem;
  line-height: 1.65;
  color: $text-body;
}

// ===== FEATURES =====
.hh-features__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.hh-feature {
  position: relative;
}

.hh-feature__soon {
  position: absolute;
  top: 24px;
  right: 24px;
  padding: 3px 10px;
  border-radius: 50px;
  background: rgba(199, 52, 104, 0.08);
  color: $brand-magenta;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

// ===== SHOWCASE =====
.hh-showcase__grid {
  display: grid;
  grid-template-columns: 300px 1fr 320px;
  gap: 40px;
  align-items: center;
}

.hh-showcase__tabs {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hh-showcase__tab {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border-radius: $radius-md;
  border: 1px solid transparent;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: all $transition-base;
  font-family: inherit;

  &:hover {
    background: $light-bg-alt;
  }

  &--active {
    background: #fff;
    border-color: rgba(108, 41, 115, 0.1);
    box-shadow: $shadow-md;

    .hh-showcase__tab-icon {
      background: $brand-gradient;
      color: #fff;
    }
  }
}

.hh-showcase__tab-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(108, 41, 115, 0.08);
  color: $brand-purple;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all $transition-base;
}

.hh-showcase__tab-text {
  display: flex;
  flex-direction: column;

  strong {
    color: $text-dark;
    font-size: 0.95rem;
  }

  span {
    color: $text-muted;
    font-size: 0.8rem;
  }
}

.hh-showcase__stage {
  position: relative;
  display: flex;
  justify-content: center;
  min-height: 640px;
  align-items: center;
}

.hh-showcase__glow {
  position: absolute;
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: $brand-gradient;
  opacity: 0.18;
  filter: blur(70px);
}

.hh-showcase__detail {
  h3 {
    margin-bottom: 12px;
  }
}

.hh-showcase__desc {
  color: $text-body;
  line-height: 1.75;
  margin-bottom: 20px;
}

.hh-check-list__item {
  display: flex;
  gap: 12px;
  padding: 8px 0;
  font-size: 0.93rem;
  color: $text-body;

  i {
    color: $brand-magenta;
    margin-top: 4px;
  }
}

.hh-swap-enter-active,
.hh-swap-leave-active {
  transition: all 0.25s ease;
}

.hh-swap-enter {
  opacity: 0;
  transform: translateY(12px);
}

.hh-swap-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

// ===== CONNECTED =====
.hh-connected__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.hh-flow {
  background: #fff;
  border-radius: $radius-lg;
  padding: 24px;
  box-shadow: $shadow-sm;
  border: 1px solid rgba(108, 41, 115, 0.06);
}

.hh-flow__trigger {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px dashed rgba(108, 41, 115, 0.15);

  strong {
    color: $text-dark;
    font-size: 1rem;
  }
}

.hh-flow__icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: $brand-gradient;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hh-flow__result {
  display: flex;
  gap: 10px;
  padding: 5px 0;
  font-size: 0.88rem;
  color: $text-body;

  i {
    color: $brand-magenta;
    margin-top: 5px;
  }
}

// ===== PRIVACY =====
.hh-privacy {
  position: relative;
}

.hh-privacy__shape {
  position: absolute;
  top: -20%;
  right: -10%;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: rgba(108, 41, 115, 0.25);
  filter: blur(90px);
  pointer-events: none;
}

.hh-privacy__grid {
  position: relative;
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 60px;
  align-items: start;
}

.hh-privacy__lead {
  margin: 16px 0 28px;
  line-height: 1.8;
}

.hh-roles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.hh-role {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: $radius-md;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);

  i {
    color: $brand-magenta;
    margin-top: 4px;
    width: 16px;
    text-align: center;
  }

  strong {
    display: block;
    color: #fff;
    font-size: 0.92rem;
  }

  span {
    color: rgba(255, 255, 255, 0.55);
    font-size: 0.8rem;
  }
}

.hh-visibility {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: $radius-xl;
  padding: 8px;
  margin-bottom: 24px;
}

.hh-visibility__item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: $radius-lg;

  & + & {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  strong {
    display: block;
    color: #fff;
  }

  span {
    color: rgba(255, 255, 255, 0.55);
    font-size: 0.85rem;
  }
}

.hh-visibility__icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: $brand-gradient;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.hh-trust__item {
  display: flex;
  gap: 12px;
  padding: 8px 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.75);

  i {
    color: #44c495;
    margin-top: 4px;
  }
}

// ===== PRICING =====
.hh-pricing__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 420px));
  justify-content: center;
  gap: 28px;
}

.hh-plan {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 36px 32px;
  border-radius: $radius-xl;
  background: #fff;
  border: 1px solid rgba(108, 41, 115, 0.12);
  box-shadow: $shadow-sm;

  &--featured {
    background: $dark-bg;
    border-color: transparent;
    box-shadow: $shadow-xl;

    .hh-plan__name,
    .hh-plan__price strong {
      color: #fff;
    }

    .hh-plan__tagline,
    .hh-plan__price span,
    .hh-plan__note {
      color: rgba(255, 255, 255, 0.6);
    }

    .hh-plan__seats {
      background: rgba(255, 255, 255, 0.06);
      color: rgba(255, 255, 255, 0.85);
    }

    .hh-plan__feature {
      color: rgba(255, 255, 255, 0.8);
    }
  }
}

.hh-plan__ribbon {
  position: absolute;
  top: -14px;
  right: 28px;
  padding: 6px 16px;
  border-radius: 50px;
  background: $brand-gradient;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  box-shadow: 0 6px 18px rgba(199, 52, 104, 0.4);
}

.hh-plan__name {
  font-family: $font-heading;
  font-size: 1.4rem;
  font-weight: 800;
  color: $text-dark;
}

.hh-plan__tagline {
  font-size: 0.9rem;
  color: $text-muted;
  margin-top: 4px;
}

.hh-plan__price {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;

  strong {
    font-family: $font-heading;
    font-size: 2.6rem;
    font-weight: 800;
    color: $text-dark;
    line-height: 1;
  }

  span {
    color: $text-muted;
    font-size: 0.9rem;
  }
}

.hh-plan__note {
  font-size: 0.82rem;
  color: $text-muted;
  margin-top: 8px;
}

.hh-plan__seats {
  margin: 20px 0;
  padding: 12px 16px;
  border-radius: $radius-md;
  background: $light-bg-alt;
  font-size: 0.9rem;
  color: $text-body;

  i {
    color: $brand-magenta;
    margin-right: 6px;
  }
}

.hh-plan__features {
  flex: 1;
  margin-bottom: 28px;
}

.hh-plan__feature {
  display: flex;
  gap: 12px;
  padding: 7px 0;
  font-size: 0.9rem;
  color: $text-body;

  i {
    color: $brand-magenta;
    margin-top: 5px;
    width: 14px;
  }

  &--off {
    opacity: 0.45;

    i {
      color: inherit;
    }
  }
}

.hh-plan__btn {
  display: block;
  text-align: center;
  padding: 14px;
  border-radius: 50px;
  border: 2px solid $brand-purple;
  font-weight: 600;
  transition: all $transition-base;

  &:hover {
    background: $brand-gradient;
    border-color: transparent;
    color: #fff !important;
  }

  &--primary {
    background: $brand-gradient;
    border-color: transparent;
    color: #fff !important;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 30px rgba(199, 52, 104, 0.45);
    }
  }
}

.v-application a.hh-plan__btn--primary {
  color: #fff !important;
}

.v-application a.hh-plan__btn:not(.hh-plan__btn--primary) {
  color: $brand-purple !important;

  &:hover {
    color: #fff !important;
  }
}

.hh-pricing__footnote {
  max-width: 720px;
  margin: 32px auto 0;
  text-align: center;
  font-size: 0.85rem;
  color: $text-muted;
}

// ===== FAQ =====
.hh-faq__grid {
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 60px;
  align-items: start;
}

.hh-faq__header {
  position: sticky;
  top: 120px;

  p {
    margin-top: 16px;
  }
}

// ===== CTA =====
.hh-cta {
  position: relative;
  padding: 100px 0;
  overflow: hidden;
}

.hh-cta__shape {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;

  &--1 {
    top: -40%;
    left: -10%;
    width: 400px;
    height: 400px;
    background: rgba(108, 41, 115, 0.25);
  }

  &--2 {
    bottom: -40%;
    right: -10%;
    width: 350px;
    height: 350px;
    background: rgba(199, 52, 104, 0.18);
  }
}

.hh-cta__content {
  position: relative;
  z-index: 2;
  max-width: 700px;
  margin: 0 auto;
  text-align: center;

  h2 {
    margin-bottom: 16px;
  }

  p {
    color: rgba(255, 255, 255, 0.65);
    margin-bottom: 32px;
  }
}

.hh-cta__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
}

// ===== RESPONSIVE =====
@media (max-width: 1199px) {
  .hh-showcase__grid {
    grid-template-columns: 260px 1fr;
  }

  .hh-showcase__detail {
    grid-column: 1 / -1;
    max-width: 640px;
    margin: 0 auto;
    text-align: center;
  }

  .hh-check-list {
    display: inline-block;
    text-align: left;
  }

  .hh-connected__grid,
  .hh-problem__grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 991px) {
  .hh-hero {
    min-height: auto;
    padding: 120px 0 60px;
  }

  .hh-hero__grid,
  .hh-privacy__grid,
  .hh-faq__grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .hh-hero__title {
    font-size: 2.5rem;
  }

  .hh-hero__visual {
    max-width: 520px;
    width: 100%;
    margin: 0 auto;
  }

  .hh-features__grid {
    grid-template-columns: 1fr 1fr;
  }

  .hh-showcase__grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .hh-showcase__tabs {
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 8px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .hh-showcase__tab {
    flex-shrink: 0;
  }

  .hh-showcase__tab-text span {
    display: none;
  }

  .hh-faq__header {
    position: static;
  }
}

@media (max-width: 767px) {
  .hh-hero__title {
    font-size: 2rem;
  }

  .hh-hero__visual {
    height: 630px;
  }

  .hh-hero__phone--back,
  .hh-float {
    display: none;
  }

  .hh-hero__phone--front {
    left: 50%;
    transform: translateX(-50%);
  }

  .hh-features__grid,
  .hh-connected__grid,
  .hh-problem__grid,
  .hh-roles {
    grid-template-columns: 1fr;
  }

  .hh-pricing__grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .hh-plan {
    padding: 32px 24px;
  }

  .hh-showcase__stage {
    min-height: 0;
  }
}
</style>
